import { UserMinusIcon, UserPlusIcon } from '@heroicons/react/24/solid';
import { CheckIcon, EnvelopeIcon, UserGroupIcon } from '@heroicons/react/16/solid';
import React from 'react';
import type { ChatActivity } from '@odo/shared-ui';

interface ActivityRowProps {
    activity: ChatActivity;
}

type Tone = 'blue' | 'purple' | 'emerald' | 'gray';

interface ActionMeta {
    Icon: typeof UserPlusIcon;
    verb: string;
    tone: Tone;
}

const ACTION_META: Record<string, ActionMeta> = {
    'chat.assign': { Icon: UserPlusIcon, verb: 'assigned', tone: 'blue' },
    'chat.unassign': { Icon: UserMinusIcon, verb: 'unassigned', tone: 'blue' },
    'chat.team.set': { Icon: UserGroupIcon, verb: 'changed team to', tone: 'purple' },
    'chat.seen': { Icon: CheckIcon, verb: 'marked the chat as read', tone: 'emerald' },
    'chat.unread': { Icon: EnvelopeIcon, verb: 'marked the chat as unread', tone: 'gray' }
};

const TONE_CLASSES: Record<Tone, string> = {
    blue: 'bg-blue-50 text-[#3773A8]',
    purple: 'bg-purple-50 text-purple-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    gray: 'bg-gray-100 text-gray-600'
};

function getActorLabel(activity: ChatActivity): string {
    return activity.actorName || activity.actorEmail || 'Someone';
}

function getMetaString(activity: ChatActivity, key: string): string | null {
    const value = activity.metadata?.[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function getTarget(activity: ChatActivity): string | null {
    switch (activity.action) {
        case 'chat.assign':
            return (
                getMetaString(activity, 'assignedToName') ||
                getMetaString(activity, 'assignedToEmail')
            );
        case 'chat.team.set':
            return getMetaString(activity, 'team');
        default:
            return null;
    }
}

function formatActivityTimestamp(iso: string): string {
    // `new Date(...)` doesn't throw on garbage — it returns an Invalid Date,
    // which `getTime()` reports as NaN. The NaN check is sufficient on its own.
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';

    const startOfDay = (d: Date): number =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const now = new Date();
    const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    if (dayDiff === 0) return time;
    if (dayDiff === 1) return `Yesterday ${time}`;

    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    }
    return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

const FALLBACK_META: ActionMeta = {
    Icon: EnvelopeIcon,
    verb: 'performed an action',
    tone: 'gray'
};

const ActivityRow: React.FC<ActivityRowProps> = ({ activity }) => {
    const meta = ACTION_META[activity.action] ?? FALLBACK_META;
    const Icon = meta.Icon;
    const actor = getActorLabel(activity);
    const target = getTarget(activity);
    const time = formatActivityTimestamp(activity.createdAt);

    const ariaLabel = [actor, meta.verb, target].filter(Boolean).join(' ');

    return (
        <div
            role="status"
            aria-label={ariaLabel}
            className="flex items-center gap-2 select-none px-1 py-1.5"
        >
            <span className="h-px flex-1 bg-gray-100" aria-hidden />
            <span
                className={`inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${TONE_CLASSES[meta.tone]}`}
                aria-hidden
            >
                {/* 16px icons render crisply on Heroicons' native sub-grid; smaller
            sizes alias diagonal strokes (notably the check mark). */}
                <Icon className="h-4 w-4" />
            </span>
            <span className="inline-flex min-w-0 items-center gap-1 text-[11px] text-gray-500">
                <span className="truncate font-semibold text-gray-700">{actor}</span>
                <span className="whitespace-nowrap">{meta.verb}</span>
                {target && <span className="truncate font-semibold text-gray-700">{target}</span>}
                {time && (
                    <>
                        <span className="text-gray-300 flex-shrink-0">·</span>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                            {time}
                        </span>
                    </>
                )}
            </span>
            <span className="h-px flex-1 bg-gray-100" aria-hidden />
        </div>
    );
};

export { ActivityRow };
